import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, MessageCircle, Phone } from "lucide-react";
import { Logo } from "@/components/Header";
import { CONTACT, SOCIALS } from "@/lib/images";
import { track } from "@/lib/track";

const NAV = [
  { label: "Menu", to: "/menu" },
  { label: "Ripe Now", to: "/ripe-now" },
  { label: "Our Story", to: "/our-story" },
  { label: "Stores", to: "/stores" },
  { label: "Franchise", to: "/franchise" },
  { label: "Contact", to: "/contact" },
];

export default function Footer() {
  return (
    <footer className="bg-deep text-cream" data-testid="site-footer">
      <div className="mx-auto max-w-[1440px] px-5 pb-28 pt-16 md:px-10 md:pb-16 md:pt-24">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <Logo light />
            <p className="mt-4 font-display text-2xl font-bold tracking-[-0.01em] text-cream md:text-3xl">
              REAL TASTES BETTER.
            </p>
            <p className="mt-4 flex items-start gap-2 text-base leading-relaxed text-cream/70">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-orange-rific" />
              T9, Prestige Fern Residency, Harlur Road, Bangalore, Karnataka – 560102, India.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a
                href={CONTACT.phoneHref}
                data-testid="footer-phone-link"
                onClick={() => track("phone_click", { placement: "footer" })}
                className="inline-flex items-center gap-1.5 rounded-full border border-cream/25 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-cream/85 transition-colors hover:border-orange-rific hover:text-orange-rific"
              >
                <Phone className="h-3.5 w-3.5" /> {CONTACT.phoneDisplay}
              </a>
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-link"
                onClick={() => track("whatsapp_click", { placement: "footer" })}
                className="inline-flex items-center gap-1.5 rounded-full border border-cream/25 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-cream/85 transition-colors hover:border-orange-rific hover:text-orange-rific"
              >
                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-orange-rific">Explore</p>
            <ul className="mt-5 space-y-3">
              {NAV.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm font-medium text-cream/85 transition-colors hover:text-orange-rific"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-orange-rific">Talk to us</p>
            <ul className="mt-5 space-y-3">
              <li>
                <a
                  href={`mailto:${CONTACT.email}`}
                  data-testid="footer-email-rific-india"
                  onClick={() => track("email_click", { placement: "footer", mailbox: "rific-india" })}
                  className="text-sm font-medium text-cream/85 transition-colors hover:text-orange-rific"
                >
                  {CONTACT.email}
                </a>
              </li>
              {SOCIALS.map((s) => (
                <li key={s.label}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-social-instagram"
                    onClick={() => track("instagram_click", { placement: "footer", network: s.label })}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-cream/85 transition-colors hover:text-orange-rific"
                  >
                    Instagram: {s.handle}
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-8 font-mono text-xs uppercase tracking-[0.25em] text-cream/50">rific.in</p>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-3 border-t border-deep-line pt-6 md:flex-row md:items-center md:justify-between">
          <p className="text-xs text-cream/50">
            © {new Date().getFullYear()} RIFIC Foods LLP. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" data-testid="footer-privacy-link" className="text-xs text-cream/50 transition-colors hover:text-cream">
              Privacy Policy
            </Link>
            <Link to="/terms" data-testid="footer-terms-link" className="text-xs text-cream/50 transition-colors hover:text-cream">
              Terms
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
